import { createUniqueId, For, Match, Show, Switch } from "solid-js";
import { useI18n } from "@/api/i18n";
import {
  COLOR_TRANSITION,
  FOCUS_RING,
} from "@/domains/freelance/taxonomy/components/sector-selector/primitives";
import {
  categoryLabel,
  subcategoriesOf,
  subcategoryLabel,
  taxonomyStatus,
} from "@/domains/freelance/taxonomy/store/taxonomy-store";
import { cn } from "@/libs/cn";
import {
  behaviourHintKey,
  type SubcategoryDraft,
} from "../../utils/subcategory-filter";

const I18N = "company.search.freelancers.result.filters.job_subcategory";

const ROW_CLASS = cn(
  "flex min-h-8 w-full items-center gap-2 rounded-md px-2 py-1 text-left text-sm text-foreground hover:bg-accent",
  COLOR_TRANSITION,
  FOCUS_RING,
);

/** The box of a row: square, because more than one item can be picked here. */
const Tick = (props: { on: boolean }) => (
  <span
    class={cn(
      "flex size-4 shrink-0 items-center justify-center rounded-[4px] border text-[10px]",
      COLOR_TRANSITION,
      props.on ? "border-ring bg-ring text-white" : "border-gray-500 bg-white",
    )}
    aria-hidden="true"
  >
    <Show when={props.on}>
      <i class="ph ph-check font-bold" />
    </Show>
  </span>
);

export type CategoryPanelProps = {
  surface: "search" | "pool";
  draft: SubcategoryDraft;
  /** The category this panel belongs to (the expanded one). */
  category: string;
  onWhole: () => void;
  onToggle: (key: string) => void;
};

/**
 * The content of the open category: the behaviour hint, «Tutta la categoria»
 * (a radio, not a toggle: pressing it again changes nothing) and the
 * subcategories to tick. While the tree loads, or when it failed, a message
 * takes the place of the subcategories; the category stays choosable.
 */
export const CategoryPanel = (props: CategoryPanelProps) => {
  const { t } = useI18n();
  const hintId = createUniqueId();

  const isChosen = () => props.draft.category === props.category;
  const whole = () => isChosen() && props.draft.subcategories.length === 0;
  const isPicked = (key: string) =>
    isChosen() && props.draft.subcategories.includes(key);
  const groupLabel = () =>
    t(`${I18N}.group_label`, { category: categoryLabel(props.category) });

  return (
    <div class="flex flex-col gap-0.5">
      <p id={hintId} class="px-2 pb-1 text-xs text-foreground/70">
        {t(behaviourHintKey(props.surface, props.draft))}
      </p>
      <div role="radiogroup" aria-label={groupLabel()}>
        {/* biome-ignore lint/a11y/useSemanticElements: same row as the subcategory buttons (D19): a native radio would need its own label and styling */}
        <button
          type="button"
          role="radio"
          class={ROW_CLASS}
          aria-checked={whole()}
          aria-describedby={hintId}
          onClick={() => props.onWhole()}
        >
          <Tick on={whole()} />
          {t(`${I18N}.whole_category`)}
        </button>
      </div>
      <Switch>
        <Match when={taxonomyStatus() === "ready"}>
          <ul class="flex flex-col" aria-describedby={hintId}>
            <For each={subcategoriesOf(props.category)}>
              {(key) => (
                <li>
                  <button
                    type="button"
                    class={ROW_CLASS}
                    aria-pressed={isPicked(key)}
                    onClick={() => props.onToggle(key)}
                  >
                    <Tick on={isPicked(key)} />
                    <span class="min-w-0">{subcategoryLabel(key)}</span>
                  </button>
                </li>
              )}
            </For>
          </ul>
        </Match>
        <Match when={taxonomyStatus() === "failed"}>
          <p class="px-2 py-1 text-xs text-foreground/70">
            {t(`${I18N}.failed`)}
          </p>
        </Match>
        <Match when={true}>
          <p class="px-2 py-1 text-xs text-foreground/70">
            {t(`${I18N}.loading`)}
          </p>
        </Match>
      </Switch>
    </div>
  );
};
